import React, { useReducer } from 'react';
import { LinearProgress } from '@mui/material';
import { DICTIONARY } from 'shared/constants';
import { LoginStage } from 'shared/enums';
import { initialLoginStage } from 'shared/utilities';
import ResetPassword from './forms/ResetPassword';
import ConfirmNewPassword from './forms/ConfirmNewPassword';
import PasswordChallenge from './forms/PasswordChallenge';
import SignIn from './forms/SignIn';
import VerifyEmail from './forms/VerifyEmail';

/**
 *
 * This component includes the logic for various login processes.
 * A useReducer manages state and the various interactions, passing
 * the state and a dispatcher to each of the respective form UIs.
 *
 * Once a user is signed in with <SignIn />, their Cognito profile is made
 * available app-wide via UserContext, and it is the AuthState value of
 * serContext that App.tsx uses to determine whether the login screen or
 * a content screen should be rendered.
 *
 * If, after signing in with <SignIn />, the user still has a password
 * challenge on their account, they will be given the prompt to change
 * their password. Otherwise they will be taken directly to their dashboard.
 *
 * Helpful tutorials for creating a custom Amplify workflow:
 * https://richardzcode.github.io/Journal-AWS-Amplify-Tutorial/step-03/
 * https://serverless-stack.com/chapters/handle-forgot-and-reset-password.html
 */

const initialState = {
  authError: '',
  challengeError: '',
  confirmError: '',
  isLoading: false,
  newPassword: '',
  password: '',
  resetCode: '',
  resetError: '',
  stage: LoginStage.SIGN_IN,
  username: '',
  verificationCode: '',
  verificationError: '',
  showPassword: false,
};

const loginReducer = (state: LoginState, action: LoginAction) => {
  switch (action.type) {
    case 'auth error': {
      return {
        ...state,
        authError: DICTIONARY.AuthError,
        isLoading: false,
        password: '',
      };
    }

    case 'cancel email verification': {
      // A fresh copy is required: this action fires while state is still the
      // very object passed to useReducer, and returning it unchanged lets
      // React bail out of the re-render that shows the sign-in form again.
      return { ...initialState };
    }

    case 'cancel password reset': {
      return {
        ...state,
        stage: LoginStage.SIGN_IN,
      };
    }

    case 'challenge error':
      return {
        ...state,
        challengeError: action.error,
        isLoading: false,
      };

    case 'challenge prompt':
      return {
        ...state,
        isLoading: false,
        stage: LoginStage.FORCE_PASSWORD,
      };

    case 'challenge success':
      return initialState;

    case 'confirm success':
      return initialState;

    case 'confirm error':
      return {
        ...state,
        isLoading: false,
        confirmError: action.error,
      };

    case 'field': {
      return {
        ...state,
        [action.fieldName]: action.payload,
      };
    }

    case 'login': {
      return {
        ...state,
        error: '',
        isLoading: true,
      };
    }

    case 'loading': {
      return {
        ...state,
        isLoading: true,
      };
    }

    case 'request password reset': {
      return {
        ...state,
        stage: LoginStage.REQUEST_RESET,
      };
    }

    case 'reset request sent': {
      return {
        ...state,
        isLoading: false,
        resetError: '',
        stage: LoginStage.RESET_SENT,
      };
    }

    case 'reset error': {
      return {
        ...state,
        isLoading: false,
        requestPasswordReset: true,
        resetCodeSent: false,
        resetError: action.error,
      };
    }

    case 'verification error': {
      return {
        ...state,
        isLoading: false,
        verificationError: action.error,
      };
    }

    case 'verification prompt': {
      return {
        ...state,
        isLoading: false,
        stage: LoginStage.VERIFY_EMAIL,
      };
    }

    case 'toggle password visibility': {
      return {
        ...state,
        showPassword: !state.showPassword,
      };
    }

    default:
      return state;
  }
};

const CustomSignIn: React.FC = () => {
  const [state, dispatch] = useReducer(loginReducer, initialState);
  const {
    isLoading,
    stage,
  } = state;
  const currentStage = initialLoginStage(stage);
  // onAuthUIStateChange((nextAuthState, authData) => {
  //   console.log('authstate changed to', nextAuthState);
  // });

  if (isLoading) {
    return <LinearProgress />;
  }

  switch (currentStage) {
    case LoginStage.REQUEST_RESET:
      return <ResetPassword state={state} dispatch={dispatch} />;

    case LoginStage.RESET_SENT:
      return <ConfirmNewPassword state={state} dispatch={dispatch} />;

    case LoginStage.FORCE_PASSWORD:
      return <PasswordChallenge state={state} dispatch={dispatch} />;

    case LoginStage.VERIFY_EMAIL:
      return <VerifyEmail state={state} dispatch={dispatch} />;

    default:
      return <SignIn state={state} dispatch={dispatch} />;
  }
};

export default CustomSignIn;
